import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../../../../constants/environment';
import SessionContext from '../../../../contexts/SessionContext';
import type { UserPermission } from '../../../../hooks/useEditDefaultPermissions';

/** Everything in every organization, or only the chosen tabs and permissions. */
export type EmployeeAccess = 'admin' | UserPermission;

export interface YaxleTeamMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    active: boolean;
    access: EmployeeAccess;
    canManageTeam: boolean;
    lastActive: string | null;
    isYou: boolean;
}

export interface YaxleTeamInvite {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    access: EmployeeAccess;
    canManageTeam: boolean;
    sentAt: string;
}

export interface YaxleTeamResponse {
    members: YaxleTeamMember[];
    invites: YaxleTeamInvite[];
    /** Inviting also sets up the Microsoft 365 account, license, and group (production). */
    microsoft365Provisioning: boolean;
}

export interface InviteEmployeeInput {
    email: string;
    /** Needed to create their Microsoft 365 account when it doesn't exist yet. */
    firstName?: string;
    lastName?: string;
    access: EmployeeAccess;
    canManageTeam: boolean;
}

/** Optional fields are missing from an API deployed before this website (it ships first). */
export interface InviteEmployeeResult {
    inviteId: string | null;
    emailSent: boolean;
    /** Not emailed because their mailbox was just set up; share the invite link instead. */
    mailboxPending?: boolean;
    microsoft365: {
        accountCreated?: boolean;
        /** Only when this invite created the account; shown once, never stored. */
        temporaryPassword?: string | null;
        licensesAssigned: number;
        groupsJoined: number;
        /** Steps Microsoft refused after the account existed; Resend retries them. */
        warnings?: string[];
    } | null;
}

export const inviteLinkFor = (inviteId: string) => `${window.location.origin}/create-account/invite/${inviteId}`;

export interface UpdateEmployeeInput {
    userId: string;
    access?: EmployeeAccess;
    canManageTeam?: boolean;
    active?: boolean;
}

const YAXLE_TEAM_KEY = 'yaxle-team';

/** The API's message for a refused team change, else a generic fallback. */
export const teamErrorMessage = (error: unknown, fallback: string): string =>
    (axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : undefined) ?? fallback;

const useAuthHeaders = () => {
    const session = useContext(SessionContext);
    return { Authorization: session?.sessionId ?? '' };
};

export const useYaxleTeam = () => {
    const session = useContext(SessionContext);
    const headers = useAuthHeaders();
    const canManage = session?.isAuthenticated === true && session.canManageInternalTeam;
    return useQuery({
        queryKey: [YAXLE_TEAM_KEY, session?.sessionId],
        queryFn: async () => (await axios.get<YaxleTeamResponse>(`${API_URL}/settings/internal/team`, { headers })).data,
        enabled: canManage,
    });
};

const useInvalidateTeam = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: [YAXLE_TEAM_KEY] });
};

/** Also resends: inviting an address with a pending invite replaces it. */
export const useInviteEmployee = () => {
    const headers = useAuthHeaders();
    const invalidate = useInvalidateTeam();
    return useMutation({
        mutationFn: async (input: InviteEmployeeInput) =>
            (await axios.post<InviteEmployeeResult>(`${API_URL}/settings/internal/team/invite`, input, { headers })).data,
        onSettled: invalidate,
    });
};

export const useUpdateEmployee = () => {
    const headers = useAuthHeaders();
    const invalidate = useInvalidateTeam();
    return useMutation({
        mutationFn: async (input: UpdateEmployeeInput) =>
            axios.patch(`${API_URL}/settings/internal/team/user`, input, { headers }),
        onSettled: invalidate,
    });
};

export const useRevokeEmployeeInvite = () => {
    const headers = useAuthHeaders();
    const invalidate = useInvalidateTeam();
    return useMutation({
        mutationFn: async (inviteId: string) =>
            axios.delete(`${API_URL}/settings/internal/team/invite`, { headers, data: { inviteId } }),
        onSettled: invalidate,
    });
};
